import { useNavigate, useSearchParams } from "@solidjs/router";
import { createEffect, createSignal, Match, onMount, Switch } from "solid-js";
import { produce } from "solid-js/store";
import { useI18n } from "@/api/i18n";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/ui/loading";
import { getVerifyErrorKind, useVerifyToken } from "@/domains/auth/api";
import {
  AuthDescription,
  AuthFormBox,
  AuthHeader,
  AuthTitle,
} from "@/domains/auth/components/AuthBoxes";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import { fetchSessionBootstrap } from "@/domains/auth/session/api";
import { resolveSessionRedirectPath } from "@/domains/auth/session/navigation";
import {
  clearSignupDraft,
  getSignupDraftForRedirect,
} from "@/domains/auth/store/signup-draft-store";
import { MetaTitleWithSeparator } from "@/domains/business/seo";
import { createSoloFreelancerWorkspace } from "@/domains/company/company/api";
import type { ProfileV2 } from "@/domains/freelance/profile/api/types";
import { usePostHog } from "@/hooks/usePostHog";
import { getSafeRedirectPath } from "@/libs/redirect";

export default function VerifyRoute() {
  const [searchParams] = useSearchParams<{
    token: string;
    callbackUrl: string;
    next: string;
  }>();

  return (
    <AuthLayout>
      <Switch>
        <Match when={!searchParams.token}>
          <InvalidToken />
        </Match>
        <Match when={searchParams.token}>
          <VerifyToken
            token={searchParams.token!}
            callbackUrl={searchParams.callbackUrl}
            next={searchParams.next}
          />
        </Match>
      </Switch>
    </AuthLayout>
  );
}

const VerifyToken = (props: {
  token: string;
  callbackUrl?: string;
  next?: string;
}) => {
  const tokenQuery = useVerifyToken(
    props.token,
    props.next ?? props.callbackUrl,
  );
  const [, setAuth] = useAuthV2();
  const { identify } = usePostHog();

  createEffect(() => {
    if (tokenQuery.isSuccess) {
      setAuth(
        produce((draft) => {
          draft.profile = {
            ...tokenQuery.data.profile,
            personal_profile_status:
              tokenQuery.data.profile.personal_profile_status ??
              tokenQuery.data.profile.onboarding_status,
          };
        }),
      );
      identify(tokenQuery.data.profile.id, {
        onboarding_status: tokenQuery.data.profile.onboarding_status,
        email: tokenQuery.data.profile.email,
        name: tokenQuery.data.profile.full_name,
        has_resume:
          !!tokenQuery.data.profile.assets &&
          tokenQuery.data.profile.assets.length > 0,
      });
    }
  });

  return (
    <Switch>
      <Match when={tokenQuery.isLoading}>
        <Loading class="h-80" />
      </Match>
      <Match when={tokenQuery.isSuccess}>
        {(() => {
          // Prefer the value the backend sanitized over our own query param.
          // The raw param stays as a fallback for responses that don't carry
          // `next` at all (older backend), where getSafeRedirectPath is the gate.
          const finalNext = tokenQuery.data?.next ?? props.next;

          return (
            <EmailValid
              callbackUrl={props.callbackUrl}
              next={finalNext}
              profile={tokenQuery.data?.profile}
            />
          );
        })()}
      </Match>
      <Match when={tokenQuery.isError}>
        {(() => {
          const kind = getVerifyErrorKind(tokenQuery.error);
          return kind === "expired" ? <ExpiredToken /> : <InvalidToken />;
        })()}
      </Match>
    </Switch>
  );
};

const EmailValid = (props: {
  callbackUrl?: string;
  next?: string;
  profile?: ProfileV2;
}) => {
  const { t } = useI18n();
  const { capture } = usePostHog();
  const [isRedirecting, setIsRedirecting] = createSignal(false);

  const preferredPath = () => props.next ?? props.callbackUrl;

  const handleRedirect = async () => {
    if (isRedirecting()) return;
    setIsRedirecting(true);
    capture("verify_email");

    const draft = getSignupDraftForRedirect();

    try {
      if (
        draft?.signupIntent === "publish" &&
        draft.publisherKind === "company"
      ) {
        clearSignupDraft();
        window.location.href = "/auth/onboarding/company";
        return;
      }

      if (
        draft?.signupIntent === "publish" &&
        draft.publisherKind === "solo_freelancer"
      ) {
        await createSoloFreelancerWorkspace({
          name: props.profile?.full_name ?? undefined,
        });
        clearSignupDraft();
        window.location.href = "/job-offers/create/ai";
        return;
      }

      const context = await fetchSessionBootstrap();
      clearSignupDraft();
      capture("start_session");
      window.location.href = resolveSessionRedirectPath(
        context,
        preferredPath(),
      );
    } catch (error) {
      console.error("Unable to resolve post-verify session redirect", error);
      clearSignupDraft();
      window.location.href = props.next
        ? getSafeRedirectPath(props.next, props.callbackUrl ?? "/")
        : props.callbackUrl
          ? getSafeRedirectPath(props.callbackUrl, "/")
          : "/auth/onboarding";
    }
  };

  createEffect(() => {
    void handleRedirect();
  });

  return (
    <AuthFormBox>
      <MetaTitleWithSeparator>
        {t("auth.verify.meta.title")}
      </MetaTitleWithSeparator>
      <img
        src="https://bucket.sevedemo.com/assets/success.png"
        alt="Email sent"
        class="w-1/3 mx-auto"
      />
      <AuthHeader class="items-center">
        <AuthTitle class="text-center">
          {t("auth.verify.accountVerified")}
        </AuthTitle>
        <AuthDescription class="text-center">
          {t("auth.verify.redirectingTo")}{" "}
          <span class="font-medium">{t("auth.verify.onboardingPage")}</span>
        </AuthDescription>
      </AuthHeader>
      <Button onClick={() => void handleRedirect()} disabled={isRedirecting()}>
        {t("auth.verify.goToOnboarding")}
      </Button>
    </AuthFormBox>
  );
};

const VerifyErrorIcon = () => (
  <div
    class="mx-auto flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
    aria-hidden
  >
    <svg
      class="h-10 w-10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
      role="img"
      aria-label="Icona stato errore verifica"
    >
      <title>Icona stato errore verifica</title>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  </div>
);

const ExpiredToken = () => {
  const navigate = useNavigate();
  const { t } = useI18n();

  onMount(() => {
    setTimeout(() => {
      navigate("/auth");
    }, 3000);
  });

  return (
    <AuthFormBox>
      <VerifyErrorIcon />
      <AuthHeader class="items-center">
        <AuthTitle class="text-center">
          {t("auth.verify.expired.title")}
        </AuthTitle>
        <AuthDescription class="text-center">
          {t("auth.verify.expired.description")}
        </AuthDescription>
        <AuthDescription class="text-center text-muted-foreground">
          {t("auth.verify.expired.redirectingToLogin")}
        </AuthDescription>
      </AuthHeader>
      <Button variant="outline" onClick={() => navigate("/auth")}>
        {t("auth.verify.backToLogin")}
      </Button>
    </AuthFormBox>
  );
};

const InvalidToken = () => {
  const navigate = useNavigate();
  const { t } = useI18n();

  onMount(() => {
    setTimeout(() => {
      navigate("/auth");
    }, 3000);
  });

  return (
    <AuthFormBox>
      <VerifyErrorIcon />
      <AuthHeader class="items-center">
        <AuthTitle class="text-center">
          {t("auth.verify.unauthorized")}
        </AuthTitle>
        <AuthDescription class="text-center">
          {t("auth.verify.redirectingToLogin")}
        </AuthDescription>
      </AuthHeader>
      <Button variant="outline" onClick={() => navigate("/auth")}>
        {t("auth.verify.backToLogin")}
      </Button>
    </AuthFormBox>
  );
};
